import { useEffect } from "react";
import { Collapse } from "antd";
import { LocaleLink as Link } from "../i18n/LocaleLink";
import { useTranslation, Trans } from "react-i18next";
import { TopNav } from "../components/nav/TopNav";
import { HeroCarousel } from "../components/common/HeroCarousel";
import "./HelpPage.less";

export function HelpPage() {
  const { t } = useTranslation("help");

  useEffect(() => {
    document.title = `${t("title")} · Daynav`;
    return () => { document.title = "Daynav"; };
  }, [t]);

  const accordionItems = [
    {
      key: "chat",
      label: t("chat.label"),
      children: (
        <div className="help-page__section">
          <p>{t("chat.intro")}</p>
          <p>
            <Trans
              i18nKey="chat.scope"
              ns="help"
              components={{ strong: <strong /> }}
            />
          </p>
        </div>
      ),
    },
    {
      key: "dictionary",
      label: t("dictionary.label"),
      children: (
        <div className="help-page__section">
          <p>{t("dictionary.intro")}</p>
          <dl className="help-page__scopes">
            <div className="help-page__scope">
              <dt>{t("dictionary.trip.term")}</dt>
              <dd>{t("dictionary.trip.definition")}</dd>
            </div>
            <div className="help-page__scope">
              <dt>{t("dictionary.destination.term")}</dt>
              <dd>{t("dictionary.destination.definition")}</dd>
            </div>
            <div className="help-page__scope">
              <dt>{t("dictionary.segment.term")}</dt>
              <dd>{t("dictionary.segment.definition")}</dd>
            </div>
            <div className="help-page__scope">
              <dt>{t("dictionary.cost.term")}</dt>
              <dd>{t("dictionary.cost.definition")}</dd>
            </div>
            <div className="help-page__scope">
              <dt>{t("dictionary.place.term")}</dt>
              <dd>{t("dictionary.place.definition")}</dd>
            </div>
          </dl>

          <h4>{t("dictionary.ownThreads")}</h4>
          <p>{t("dictionary.ownThreadsText")}</p>

          <h4>{t("dictionary.scopeChip")}</h4>
          <p>{t("dictionary.scopeChipText")}</p>

          <h4>{t("dictionary.autoSwitch")}</h4>
          <p>{t("dictionary.autoSwitchText")}</p>
        </div>
      ),
    },
    {
      key: "places",
      label: t("places.label"),
      children: (
        <div className="help-page__section">
          <p>{t("places.intro")}</p>

          <h4>{t("places.center")}</h4>
          <p>
            <Trans
              i18nKey="places.centerText"
              ns="help"
              components={{ strong: <strong /> }}
            />
          </p>

          <h4>{t("places.idea")}</h4>
          <p>
            <Trans
              i18nKey="places.ideaText"
              ns="help"
              components={{ strong: <strong /> }}
            />
          </p>

          <h4>{t("places.planned")}</h4>
          <p>
            <Trans
              i18nKey="places.plannedText"
              ns="help"
              components={{ strong: <strong /> }}
            />
          </p>

          <h4>{t("places.booked")}</h4>
          <p>
            <Trans
              i18nKey="places.bookedText"
              ns="help"
              components={{ strong: <strong /> }}
            />
          </p>
        </div>
      ),
    },
    {
      key: "nights",
      label: t("nights.label"),
      children: (
        <div className="help-page__section">
          <p>{t("nights.intro")}</p>
          <ul>
            <li>{t("nights.conservation")}</li>
            <li>{t("nights.locked")}</li>
            <li>{t("nights.lastZero")}</li>
          </ul>
        </div>
      ),
    },
    {
      key: "sharing",
      label: t("sharing.label"),
      children: (
        <div className="help-page__section">
          <p>{t("sharing.privacy")}</p>

          <h4>{t("sharing.collaboratorsHeading")}</h4>
          <p>
            <Trans
              i18nKey="sharing.collaboratorsText"
              ns="help"
              components={{ strong: <strong /> }}
            />
          </p>

          <h4>{t("sharing.shareLinksHeading")}</h4>
          <p>{t("sharing.shareLinksText")}</p>

          <h4>{t("sharing.togglesHeading")}</h4>
          <p>{t("sharing.togglesIntro")}</p>
          <ul className="help-page__toggles">
            <li>
              <Trans
                i18nKey="sharing.toggleUserInfo"
                ns="help"
                components={{ strong: <strong /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey="sharing.toggleCost"
                ns="help"
                components={{ strong: <strong /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey="sharing.togglePromo"
                ns="help"
                components={{ strong: <strong /> }}
              />
            </li>
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div className="help-page">
      <section className="help-hero" data-testid="help-hero">
        <HeroCarousel folder="help" />
        <div className="help-hero__scrim" />
        <TopNav overlay />
        <div className="help-hero__inner">
          <div className="help-hero__content">
            <p className="help-hero__eyebrow">{t("eyebrow")}</p>
            <h1 className="help-hero__title">{t("headline")}</h1>
          </div>
        </div>
      </section>

      <main className="help-page__content">
        <Link to="/" className="help-page__back">
          &larr; {t("backToDaynav")}
        </Link>

        <div className="help-page__notice" role="status">
          <Trans
            i18nKey="notice.text"
            ns="help"
            components={{ em: <em /> }}
            parent="span"
          />
        </div>

        <Collapse
          accordion
          defaultActiveKey="chat"
          className="help-page__accordion"
          items={accordionItems}
        />
      </main>
    </div>
  );
}
